import { ReactElement, ReactNode } from 'react'
import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'

export interface DenseTableProps {
  /** Column headers; every column after the first is right-aligned unless `columnAlign` says so. */
  headers: ReactNode[]
  rows: ReactNode[][]
  emptyMessage: string
  /** Per-column alignment, for tables whose trailing columns are text rather than numbers. */
  columnAlign?: ('left' | 'right')[]
}

/** Compact read-only table for dense numeric result sections (anchors, shear anchors, …). */
export const DenseTable = ({
  headers,
  rows,
  emptyMessage,
  columnAlign,
}: DenseTableProps): ReactElement => {
  const alignOf = (index: number): 'left' | 'right' =>
    columnAlign?.[index] ?? (index === 0 ? 'left' : 'right')

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
        {emptyMessage}
      </Typography>
    )
  }

  return (
    <Table size="small" sx={{ '& td, & th': { px: 1, py: 0.5, fontSize: 12 } }}>
      <TableHead>
        <TableRow>
          {headers.map((header, index) => (
            <TableCell
              key={index}
              align={alignOf(index)}
              sx={{ color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {header}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, rowIndex) => (
          <TableRow key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <TableCell
                key={cellIndex}
                align={alignOf(cellIndex)}
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {cell}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
